import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('breathingTubePreferences');
export interface FullBreathingTubeDetails {
  specificInterventionsToDecline?: string;
  discussedWithHealthcareProvider?: boolean;
  conditionsForBreathingTube?: string;
  additionalInstructions?: string;
}

export interface LimitedBreathingTubeDetails {
  acceptableInterventions?: string;
  limitedBreathingTubeConditions?: string;
  medicationPreferences?: string;
  timeLimits?: string;
}

export interface SpecificMedicalSituationDetails {
  triggeringConditions?: string;
  exceptions?: string;
  treatmentPreferences?: string;
  discussedWithDoctor?: boolean;
}

export interface NaturalBreathingDetails {
  comfortCarePreferences?: string;
  painManagementOptions?: string;
  spiritualCulturalPractices?: string;
  environmentPreferences?: string;
}

export type BreathingTubeDetails =
  | FullBreathingTubeDetails
  | LimitedBreathingTubeDetails
  | SpecificMedicalSituationDetails
  | NaturalBreathingDetails
  | null;

export interface BreathingTubePreferencesInfoRec {
  id: string;
  userId: string;
  breathingTubeType?: string;
  locationOfDirective?: string;
  specialInstructions?: string;
  breathingTubeDetails?: BreathingTubeDetails;
  notes?: string;
  isLocked?: boolean;
}

class BreathingTubePreferencesInfoService {
  async getAllBreathingTubePreferences(): Promise<BreathingTubePreferencesInfoRec[]> {
    const response = await http.get('/api/breathing-tube-preferences');

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch breathing tube preferences information');
    }

    return response.data.data || [];
  }

  async getBreathingTubePreferencesById(id: string): Promise<BreathingTubePreferencesInfoRec | null> {
    try {
      const response = await http.get(`/api/breathing-tube-preferences/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch breathing tube preferences information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching breathing tube preferences information by ID:', error);
      return null;
    }
  }

  async createBreathingTubePreferences(breathingTubeData: Partial<BreathingTubePreferencesInfoRec>): Promise<BreathingTubePreferencesInfoRec> {
    const response = await http.post('/api/breathing-tube-preferences', breathingTubeData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create breathing tube preferences information');
    }

    return response.data.data;
  }

  async updateBreathingTubePreferences(id: string, breathingTubeData: Partial<BreathingTubePreferencesInfoRec>): Promise<BreathingTubePreferencesInfoRec> {
    const response = await http.patch(`/api/breathing-tube-preferences/${id}`, breathingTubeData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update breathing tube preferences information');
    }

    return response.data.data;
  }

  async deleteBreathingTubePreferences(id: string): Promise<void> {
    const response = await http.delete(`/api/breathing-tube-preferences/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete breathing tube preferences information');
    }
  }

  async lockBreathingTubePreferences(id: string): Promise<void> {
    const response = await http.post(`/api/breathing-tube-preferences/${id}/lock`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock breathing tube preferences information');
    }
  }

  async unlockBreathingTubePreferences(id: string, emails: string[]): Promise<void> {
    const response = await http.post(`/api/breathing-tube-preferences/${id}/unlock`, { emails });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock breathing tube preferences information');
    }
  }

  async getBreathingTubePreferencesAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/breathing-tube-preferences/${id}/grants`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to get breathing tube preferences access grants');
      }

      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching breathing tube preferences access grants:', error);
      throw error;
    }
  }
}

export const breathingTubePreferencesService = new BreathingTubePreferencesInfoService();